import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import classNames from "classnames";
import {
    InvoicesByCounterpartyItem,
    InvoiceByCounterpartySortValue,
    InvoiceStatus,
} from "../../common/types/invoice";
import { SortableHeader } from "../general/SortableHeader/SortableHeader";
import { Amount } from "../general/Amount/Amount";
import { InvoiceFilters } from "../../common/contracts/invoice.contract";
import { ClickableContainer } from "../general/ClickableContainer";
import { pluralize } from "../../common/helpers/string";
import { Loader } from "../general/Loader";
import { EntityIconList } from "../general/EntityIconList/EntityIconList";
import { useEntities } from "../../hooks/useEntities";
import { Entity } from "../../common/types/entity";
import { CopyOnClick } from "../general/CopyOnClick";
import styles from "./InvoicesByCounterpartyTable.module.scss";

const DYNAMIC_VISIBILITY_COLUMNS: Array<keyof InvoicesByCounterpartyItem> = [
    "notYetDue",
    "pastDue1To30",
    "pastDue31To60",
    "pastDue61Plus",
    "open",
    "paid",
] as const;

interface Props {
    invoicesByCounterparty: InvoicesByCounterpartyItem[];
    sort: InvoiceByCounterpartySortValue;
    onSortChange: (sort: InvoiceByCounterpartySortValue) => void;
    isLoading: boolean;
    pagination?: React.ReactNode;
    onDrilldown: (params: InvoiceFilters) => void;
    counterpartiesCount: number;
}

interface DrilldownCellProps {
    amount: number;
    onClick: () => void;
    formatCurrency?: boolean;
}

const DrilldownCell: React.FC<DrilldownCellProps> = ({
    amount,
    onClick,
    formatCurrency = true,
}) => {
    if (amount.toString() === "0") {
        return <span className={styles.zeroAmount}>-</span>;
    }

    return (
        <ClickableContainer onClick={onClick}>
            {formatCurrency ? (
                <Amount
                    className={classNames(
                        styles.clickableCellContent,
                        styles.rightAligned,
                    )}
                    amount={amount}
                />
            ) : (
                <span
                    className={classNames(
                        styles.clickableCellContent,
                        styles.rightAligned,
                    )}
                >
                    {amount}
                </span>
            )}
        </ClickableContainer>
    );
};

export const InvoicesByCounterpartyTable: React.FC<Props> = ({
    invoicesByCounterparty: initialInvoicesByCounterparty,
    sort,
    onSortChange,
    isLoading,
    onDrilldown,
    counterpartiesCount: initialCounterpartiesCount,
    pagination,
}) => {
    const entities = useEntities();

    const getEntities = useCallback(
        (entityIds: number[]) =>
            entityIds.map(
                (entityId) => entities.find((e) => e.id === entityId) as Entity,
            ),
        [entities],
    );

    const [invoicesByCounterparty, setInvoicesByCounterparty] = useState(
        initialInvoicesByCounterparty,
    );
    const [counterpartiesCount, setCounterpartiesCount] = useState(
        initialCounterpartiesCount,
    );

    const handleDrilldown = (
        counterpartyId: string | null,
        params: InvoiceFilters,
    ) => {
        if (counterpartyId === null) {
            onDrilldown({
                ...params,
                withEmptyOrUnverifiedCounterparty: true,
            });
        } else {
            onDrilldown({
                ...params,
                counterpartiesIds: [counterpartyId],
            });
        }
    };

    useEffect(() => {
        if (!isLoading) {
            setInvoicesByCounterparty(initialInvoicesByCounterparty);
            setCounterpartiesCount(initialCounterpartiesCount);
        }
    }, [initialInvoicesByCounterparty, initialCounterpartiesCount, isLoading]);

    const columnVisibility = useMemo(() => {
        if (invoicesByCounterparty.length === 0) {
            return Object.fromEntries(
                DYNAMIC_VISIBILITY_COLUMNS.map((col) => [col, true]),
            );
        }

        return Object.fromEntries(
            DYNAMIC_VISIBILITY_COLUMNS.map((col) => [
                col,
                invoicesByCounterparty.some(
                    (item) => item[col]?.toString() !== "0",
                ),
            ]),
        );
    }, [invoicesByCounterparty]);

    return (
        <div>
            <Table responsive className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.customerColumn}>
                            <SortableHeader
                                sort={sort}
                                onChange={onSortChange}
                                asc={
                                    InvoiceByCounterpartySortValue.COUNTERPARTY_ASC
                                }
                                desc={
                                    InvoiceByCounterpartySortValue.COUNTERPARTY_DESC
                                }
                                defaultOrder="asc"
                            >
                                Customer
                            </SortableHeader>
                        </th>
                        {columnVisibility.notYetDue && (
                            <th className={styles.amountColumn}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.NOT_YET_DUE_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.NOT_YET_DUE_DESC
                                    }
                                    defaultOrder="desc"
                                    className={styles.statusTier}
                                >
                                    <span
                                        className={classNames(
                                            styles.statusDot,
                                            styles.tier1,
                                        )}
                                    />
                                    <span>Not yet due</span>
                                </SortableHeader>
                            </th>
                        )}
                        {columnVisibility.pastDue1To30 && (
                            <th className={styles.amountColumn}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_1_30_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_1_30_DESC
                                    }
                                    defaultOrder="desc"
                                    className={styles.statusTier}
                                >
                                    <span
                                        className={classNames(
                                            styles.statusDot,
                                            styles.tier2,
                                        )}
                                    />
                                    <span>1-30d</span>
                                </SortableHeader>
                            </th>
                        )}
                        {columnVisibility.pastDue31To60 && (
                            <th className={styles.amountColumn}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_31_60_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_31_60_DESC
                                    }
                                    defaultOrder="desc"
                                    className={styles.statusTier}
                                >
                                    <span
                                        className={classNames(
                                            styles.statusDot,
                                            styles.tier3,
                                        )}
                                    />
                                    <span>31-60d</span>
                                </SortableHeader>
                            </th>
                        )}
                        {columnVisibility.pastDue61Plus && (
                            <th className={styles.amountColumn}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_61_PLUS_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.PAST_DUE_61_PLUS_DESC
                                    }
                                    defaultOrder="desc"
                                    className={styles.statusTier}
                                >
                                    <span
                                        className={classNames(
                                            styles.statusDot,
                                            styles.tier4,
                                        )}
                                    />
                                    <span>61d+</span>
                                </SortableHeader>
                            </th>
                        )}
                        {columnVisibility.open && (
                            <th className={styles.totalAmount}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.OPEN_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.OPEN_DESC
                                    }
                                    defaultOrder="desc"
                                >
                                    Total open
                                </SortableHeader>
                            </th>
                        )}
                        {columnVisibility.paid && (
                            <th className={styles.totalAmount}>
                                <SortableHeader
                                    sort={sort}
                                    onChange={onSortChange}
                                    asc={
                                        InvoiceByCounterpartySortValue.PAID_ASC
                                    }
                                    desc={
                                        InvoiceByCounterpartySortValue.PAID_DESC
                                    }
                                    defaultOrder="desc"
                                >
                                    Total paid
                                </SortableHeader>
                            </th>
                        )}
                        <th className={styles.invoicesCount}>
                            <SortableHeader
                                sort={sort}
                                onChange={onSortChange}
                                asc={
                                    InvoiceByCounterpartySortValue.INVOICES_COUNT_ASC
                                }
                                desc={
                                    InvoiceByCounterpartySortValue.INVOICES_COUNT_DESC
                                }
                                defaultOrder="desc"
                            >
                                Invoices
                            </SortableHeader>
                        </th>
                        <th className={styles.entities}></th>
                    </tr>
                </thead>
                <tbody>
                    {invoicesByCounterparty.map((item) => (
                        <tr key={item.counterpartyId}>
                            <td className={styles.customerColumn}>
                                <div className={styles.customerContent}>
                                    {item.counterparty ? (
                                        <>
                                            <span
                                                className={styles.customerName}
                                            >
                                                {item.counterparty.name}
                                            </span>
                                            {item.counterparty.email && (
                                                <CopyOnClick
                                                    className={classNames(
                                                        styles.clickableCellContent,
                                                        styles.customerEmail,
                                                        styles.leftAligned,
                                                    )}
                                                    value={
                                                        item.counterparty.email
                                                    }
                                                    tooltipText="Copy email"
                                                >
                                                    {item.counterparty.email}
                                                </CopyOnClick>
                                            )}
                                        </>
                                    ) : (
                                        <span className={styles.noCustomer}>
                                            No customer
                                        </span>
                                    )}
                                </div>
                            </td>
                            {columnVisibility.notYetDue && (
                                <td className={styles.amountColumn}>
                                    <DrilldownCell
                                        amount={item.notYetDue}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.OPEN,
                                                    ],
                                                    overdue: false,
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            {columnVisibility.pastDue1To30 && (
                                <td className={styles.amountColumn}>
                                    <DrilldownCell
                                        amount={item.pastDue1To30}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.OPEN,
                                                    ],
                                                    overdue: true,
                                                    minOverdue: 1,
                                                    maxOverdue: 30,
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            {columnVisibility.pastDue31To60 && (
                                <td className={styles.amountColumn}>
                                    <DrilldownCell
                                        amount={item.pastDue31To60}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.OPEN,
                                                    ],
                                                    overdue: true,
                                                    minOverdue: 30,
                                                    maxOverdue: 60,
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            {columnVisibility.pastDue61Plus && (
                                <td className={styles.amountColumn}>
                                    <DrilldownCell
                                        amount={item.pastDue61Plus}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.OPEN,
                                                    ],
                                                    overdue: true,
                                                    minOverdue: 60,
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            {columnVisibility.open && (
                                <td className={styles.totalAmount}>
                                    <DrilldownCell
                                        amount={item.open}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.OPEN,
                                                    ],
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            {columnVisibility.paid && (
                                <td className={styles.totalAmount}>
                                    <DrilldownCell
                                        amount={item.paid}
                                        onClick={() =>
                                            handleDrilldown(
                                                item.counterpartyId,
                                                {
                                                    invoiceStatuses: [
                                                        InvoiceStatus.PAID,
                                                    ],
                                                },
                                            )
                                        }
                                    />
                                </td>
                            )}
                            <td className={styles.invoicesCount}>
                                <DrilldownCell
                                    amount={item.invoiceCount}
                                    onClick={() =>
                                        handleDrilldown(item.counterpartyId, {})
                                    }
                                    formatCurrency={false}
                                />
                            </td>
                            <td className={styles.entities}>
                                <EntityIconList
                                    entities={getEntities(item.entityIds)}
                                    showLabel={false}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className={styles.tableFooter}>
                    <tr className={styles.tableRow}>
                        <td colSpan={99}>
                            <div className={styles.tableRowContent}>
                                <div className="d-flex gap-2">
                                    {counterpartiesCount}{" "}
                                    {pluralize("customer", counterpartiesCount)}
                                </div>
                                {isLoading ? (
                                    <Loader inline delayed />
                                ) : (
                                    pagination
                                )}
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </Table>
        </div>
    );
};
